import {assign, forEach, forOwn, isArray} from 'lodash';

const KEYWORD_SCHEMA_CORE = 'core';
const KEYWORD_SCHEMA_DEPENDENCIES = 'dependencies';

const KEYWORD_PROP_VALIDATION = 'validation';

const KEYWORD_COMP_ID = 'id';
const KEYWORD_COMP_PROPERTIES = 'properties';
const KEYWORD_COMP_TYPE = 'type';
const KEYWORD_COMP_TO_STRING = 'toString';

const KEYWORD_PROP_CONSTRAINTS = 'constraints';
export const KEYWORD_PROP_CONSTRAINTS_RULE_TYPE = 'ruleType';
export const KEYWORD_PROP_CONSTRAINTS_RULE_TYPE_ENUM = 'enum';
export const KEYWORD_PROP_CONSTRAINTS_LIST_SOURCE = 'listSource';
export const KEYWORD_PROP_CONSTRAINTS_LIST_SOURCE_JSON = 'json';
export const KEYWORD_PROP_CONSTRAINTS_LIST_SOURCE_XHR = 'xhr';
export const KEYWORD_PROP_CONSTRAINTS_LIST_REF = 'listRef';
export const KEYWORD_PROP_REF = '$ref';
const KEYWORD_PROP_TYPE = 'type';
const KEYWORD_PROP_DEFAULT = 'default';
const KEYWORD_PROP_REQUIRED = 'required';

const KEYWORD_PROP_VALIDATOR = 'validator';
const KEYWORD_PROP_CONDITIONS = 'conditions';
const KEYWORD_PROP_ARRAY_VALIDATOR = 'arrayValidator';

export class ContentProperty {
  constructor(propertyContentDefinition) {
    this[KEYWORD_PROP_REQUIRED] = false;
    assign(this, propertyContentDefinition);
    this.initValidators();
    this.initArrayValidators();
    this.initConditions();
  }

  initValidators() {
    const validators = [];
    // copys validator definition, eg.
    // "validator": [{"name":"required",
    // "message":"hai.miniScreen.hai.state.validation.required"}]
    if (this[KEYWORD_PROP_VALIDATOR]) {
      forEach(this[KEYWORD_PROP_VALIDATOR], (validator) => {
        validators.push({...validator});
      });
      this[KEYWORD_PROP_VALIDATOR] = validators;
    }
  }

  initArrayValidators() {
    const arrayValidators = [];
    if (isArray(this[KEYWORD_PROP_ARRAY_VALIDATOR])) {
      forEach(this[KEYWORD_PROP_ARRAY_VALIDATOR], (validator) => {
        arrayValidators.push({...validator});
      });
      this[KEYWORD_PROP_ARRAY_VALIDATOR] = arrayValidators;
    }
  }

  initConditions() {
    if (this[KEYWORD_PROP_CONDITIONS]) {
      const conditions = [];
      forEach(this[KEYWORD_PROP_CONDITIONS], (condition) => {
        conditions.push({...condition});
      });
      this[KEYWORD_PROP_CONDITIONS] = conditions;
    }
  }

  /**
   *
   * @returns {ContentProperty.type|string}
   */
  getType() {
    return this[KEYWORD_PROP_TYPE];
  }

  /**
   *
   * @returns {ContentProperty.constraints|Object}
   */
  getConstraints() {
    return this[KEYWORD_PROP_CONSTRAINTS];
  }

  /**
   *
   * @returns {ContentProperty.validation|Object}
   */
  getValidators() {
    return this[KEYWORD_PROP_VALIDATOR];
  }

  isConditional() {
    return isArray(this[KEYWORD_PROP_CONDITIONS]);
  }

  getConditions() {
    return this[KEYWORD_PROP_CONDITIONS];
  }

  getArrayValidators() {
    return this[KEYWORD_PROP_ARRAY_VALIDATOR];
  }

  /**
   *
   * @returns {ContentProperty.$ref|string}
   */
  getRef() {
    return this[KEYWORD_PROP_REF];
  }

  /**
   *
   * @returns {ContentProperty.default|string}
   */
  getDefault() {
    return this[KEYWORD_PROP_DEFAULT];
  }

  /**
   *
   * @returns {ContentProperty.required|boolean}
   */
  getRequired() {
    return this[KEYWORD_PROP_REQUIRED];
  }
}

export class ContentComponent {
  constructor(componentContentDefinition) {
    this[KEYWORD_PROP_VALIDATION] = false;
    assign(this, componentContentDefinition);

    this.extendsProperties();
  }

  extendsProperties() {
    forOwn(this[KEYWORD_COMP_PROPERTIES], (value, key) => {
      this[KEYWORD_COMP_PROPERTIES][key] = new ContentProperty(value);
    });
  }

  /**
   *
   * @returns {ContentComponent.id|string}
   */
  getId() {
    return this[KEYWORD_COMP_ID];
  }

  /**
   *
   * @returns {ContentComponent.validation|string}
   */
  isValidated() {
    return this[KEYWORD_PROP_VALIDATION];
  }

  /**
   *
   * @param {string} propertyId
   * @returns {ContentProperty|Object}
   */
  getProperty(propertyId) {
    return this[KEYWORD_COMP_PROPERTIES][propertyId];
  }

  /**
   *
   * @returns {Object}
   */
  getProperties() {
    return this[KEYWORD_COMP_PROPERTIES];
  }

  /**
   *
   * @returns {ContentComponent.type|string}
   */
  getType() {
    return this[KEYWORD_COMP_TYPE];
  }

  /**
   *
   * @returns {ContentComponent.toString|string}
   */
  getToString() {
    return this[KEYWORD_COMP_TO_STRING];
  }

  /**
   * Gets content property definition by pathToData reference.
   *
   * @param {string} pathToData
   * @param {ContentSchema} rootContentDef
   * @returns {{isArray: boolean, property: ContentProperty}} content property definition
   */
  getPropertyByPathToData(pathToData, rootContentDef) {
    const dotIndex = pathToData.indexOf('.');
    const openBracketIndex = pathToData.indexOf('[');
    const result = {property: undefined, isArray: false};
    if (dotIndex < openBracketIndex) {
      const componentProperty = pathToData.substring(0, dotIndex);
      const componentPathToData = pathToData.substring(dotIndex + 1);
      const component = rootContentDef.getComponent(
        this.getProperty(componentProperty).getRef()
      );
      result.property = component.getPropertyByPathToData(componentPathToData);
    } else if (openBracketIndex < dotIndex) {
      const componentProperty = pathToData.substring(0, openBracketIndex);
      const componentPathToData = pathToData.substring(dotIndex + 1);
      const component = rootContentDef.getComponent(
        this.getProperty(componentProperty).getRef()
      );
      result.property = component.getPropertyByPathToData(componentPathToData);
    } else {
      // dotIndex === -1 && openBracketIndex === -1
      result.property = this.getProperty(pathToData);
      if (result.property.getRef()) {
        const refComponent = rootContentDef.getComponent(
          result.property.getRef()
        );
        if (refComponent.getType() === 'array') {
          result.isArray = true;
          // get array item properties
          result.property = refComponent.getProperties();
        } else {
          result.property = refComponent;
        }
      }
    }
    return result;
  }
}

export class ContentSchema {
  constructor(ContentDefinition) {
    assign(this, ContentDefinition);

    this.extendsComponents();
  }

  extendsComponents() {
    this[KEYWORD_SCHEMA_CORE] = new ContentComponent(this.getCore());

    forOwn(this[KEYWORD_SCHEMA_DEPENDENCIES], (value, key) => {
      this[KEYWORD_SCHEMA_DEPENDENCIES][key] = new ContentComponent(value);
    });
  }

  /**
   * Gets core definition of screen Content.
   * @returns {ContentSchema.core|Object} Core definition of screen Content.
   */
  getCore() {
    return this[KEYWORD_SCHEMA_CORE];
  }

  /**
   * Gets definition of dependencies of the screen Content.
   * @returns {ContentSchema.dependencies|Object} Definition of the screen Content dependencies.
   */
  getDependencies() {
    return this[KEYWORD_SCHEMA_DEPENDENCIES];
  }

  /**
   * Gets definition of component Content.
   * @param componentId Identifier of component.
   * @returns {ContentComponent|Object} Definition of component Content.
   */
  getComponent(componentId) {
    let componentContent;
    if (this.getCore()[KEYWORD_COMP_ID] === componentId) {
      componentContent = this.getCore();
    } else {
      // get content from dependencies
      componentContent = this[KEYWORD_SCHEMA_DEPENDENCIES][componentId];
    }
    return componentContent;
  }
}

export default {ContentSchema, ContentComponent, ContentProperty};
